import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy, Download, Webhook } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/hooks/use-toast';
import {
  IntegrationPostInstall,
  IntegrationPostInstallEndpoint,
  IntegrationPostInstallManifestAsset,
} from '../types/integration';

interface IntegrationPostInstallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  integrationLabel: string;
  postInstall: IntegrationPostInstall;
}

export const IntegrationPostInstallDialog = ({
  isOpen,
  onClose,
  integrationLabel,
  postInstall,
}: IntegrationPostInstallDialogProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [zipDownloading, setZipDownloading] = useState(false);

  const lang = i18n.language.split('-')[0];
  const steps =
    postInstall.setup_instructions?.[lang] ??
    postInstall.setup_instructions?.en ??
    postInstall.setup_instructions?.pt ??
    [];

  const endpoints = resolveEndpoints(postInstall);
  const manifestText = postInstall.manifest
    ? JSON.stringify(postInstall.manifest, null, 2)
    : null;
  const showManifestPreview = manifestText !== null && postInstall.show_manifest_preview === true;

  const copyToClipboard = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast({ title: t('integrations.postInstall.copied', 'URL copiada') });
      setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 2000);
    } catch {
      toast({
        title: t('integrations.postInstall.copyFailed', 'Falha ao copiar'),
        variant: 'destructive',
      });
    }
  };

  const downloadZip = async () => {
    if (!postInstall.manifest) return;
    setZipDownloading(true);
    try {
      await buildAndDownloadZip(
        postInstall.manifest,
        postInstall.manifest_assets ?? [],
        slugify(integrationLabel),
        msg => toast({
          title: msg,
          variant: 'destructive',
        }),
      );
    } catch (error) {
      toast({
        title: t(
          'integrations.postInstall.zipFailed',
          'Falha ao gerar o pacote ZIP',
        ),
        variant: 'destructive',
      });
    } finally {
      setZipDownloading(false);
    }
  };

  const renderI18n = (
    map: { pt?: string; en?: string; [k: string]: string | undefined } | undefined,
    fallback: string,
  ): string => map?.[lang] ?? map?.en ?? map?.pt ?? fallback;

  /**
   * Resolves a UI label first from `postInstall.labels[key]` (per-template, i18n),
   * then falls back to the i18n key with the built-in default. This makes the
   * dialog entirely data-driven while keeping back-compat with templates that
   * don't declare custom labels.
   */
  const label = (key: keyof NonNullable<IntegrationPostInstall['labels']>, i18nKey: string, fallback: string): string => {
    const map = postInstall.labels?.[key];
    if (map) return renderI18n(map, fallback);
    return t(i18nKey, fallback);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            {label('title', 'integrations.postInstall.title', 'Ferramenta criada — próximos passos')}
          </DialogTitle>
          <DialogDescription>
            {label('subtitle', 'integrations.postInstall.subtitle',
              'Configure {{name}} usando os valores abaixo e siga as instruções para concluir a instalação.')
              .replace('{{name}}', integrationLabel)}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="space-y-4">
            {endpoints.length > 0 && (
              <div className="space-y-3">
                {endpoints.map((ep) => (
                  <div key={ep.key} className="space-y-1">
                    <Label>{renderI18n(ep.label, ep.key)}</Label>
                    <div className="flex gap-2 items-stretch">
                      <Input
                        readOnly
                        value={ep.value}
                        className="font-mono text-xs flex-1 min-w-0"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => copyToClipboard(ep.key, ep.value)}
                        aria-label={label('copy', 'integrations.postInstall.copy', 'Copiar')}
                      >
                        {copiedKey === ep.key ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    {ep.description && (
                      <p className="text-xs text-muted-foreground">
                        {renderI18n(ep.description, '')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {manifestText && (
              <div className="space-y-2">
                <Label>{label('manifest', 'integrations.postInstall.manifestLabel', 'App Manifest')}</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={downloadZip}
                    disabled={zipDownloading}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {zipDownloading
                      ? t('integrations.postInstall.zipBuilding', 'Gerando...')
                      : label('download_zip', 'integrations.postInstall.downloadZip', 'Baixar ZIP')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard('manifest', manifestText)}
                  >
                    {copiedKey === 'manifest' ? (
                      <Check className="h-4 w-4 mr-1 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    {label('copy_manifest', 'integrations.postInstall.copyManifest', 'Copiar JSON')}
                  </Button>
                </div>
                {showManifestPreview && (
                  <pre className="rounded-md border bg-muted/30 p-3 text-xs overflow-x-auto max-h-[260px] font-mono">
                    {manifestText}
                  </pre>
                )}
              </div>
            )}

            {steps.length > 0 && (
              <div className="space-y-2">
                <Label>
                  {label('instructions', 'integrations.postInstall.instructionsLabel', 'Instruções de configuração')}
                </Label>
                <div className="rounded-md border bg-muted/30 p-4 text-sm leading-relaxed
                                [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h2:first-child]:mt-0
                                [&_p]:my-1
                                [&_strong]:font-semibold
                                [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs
                                [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2
                                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-0.5
                                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-0.5">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {steps.join('\n')}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={onClose}>
            {label('confirm', 'integrations.postInstall.confirm', 'Entendi')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveEndpoints(postInstall: IntegrationPostInstall): IntegrationPostInstallEndpoint[] {
  if (postInstall.endpoints && postInstall.endpoints.length > 0) {
    return postInstall.endpoints;
  }
  if (postInstall.webhook_url) {
    return [{
      key: 'webhook',
      label: { en: 'Webhook URL', pt: 'URL do Webhook' },
      value: postInstall.webhook_url,
    }];
  }
  return [];
}

function slugify(value: string): string {
  return (value || 'integration')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'integration';
}

async function buildAndDownloadZip(
  manifest: Record<string, unknown>,
  assets: IntegrationPostInstallManifestAsset[],
  slug: string,
  reportWarning: (msg: string) => void,
): Promise<void> {
  const zip = new JSZip();
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  const fetched = await Promise.all(
    assets.map(async (asset) => {
      try {
        const res = await fetch(asset.url, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return { filename: asset.filename, buf: await res.arrayBuffer() };
      } catch (error) {
        reportWarning(
          `Não foi possível baixar ${asset.filename} de ${asset.url}: ${
            (error as Error).message
          }`,
        );
        return null;
      }
    }),
  );

  for (const item of fetched) {
    if (item) zip.file(item.filename, item.buf);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `prodgy-${slug}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
